import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Link, Stack, Typography } from '@mui/material';

import useApi from 'hooks/useApi';
import { PERMISSIONS } from 'contexts/PermissionContext';
import { PermissionWrapper } from 'components/PermissionComponents';
import { CONSENT_TYPE_LABELS, ConsentState, RecordSmsConsentPayload, ResourceConsentDetails, ResourceConsentsSummary } from 'types/legal';
import { describeConsent } from './ConsentStatus';
import ConsentHistoryDialog from './ConsentHistoryDialog';
import RecordSmsConsentDialog from './RecordSmsConsentDialog';
import { errorMessage, personName, personPhone, samePhone, unwrapEnvelope } from './legalUtils';

interface ResourceConsentSectionProps {
  // ResourceData uid (the `id` the resource endpoints return).
  resourceUid: string;
  // The resource's data — phone number, country code and name come from here.
  data: Record<string, any>;
  consents?: ResourceConsentsSummary | null;
  // Called after an admin record is saved, so the list can refresh its badges.
  onChanged?: () => void;
}

/**
 * Consent status for one person inside the resource view/edit dialogs, plus the admin
 * SMS recording used for drivers who never log in. The SMS part only appears when the
 * person has a phone number and the client has SMS consent switched on.
 */
const ResourceConsentSection: React.FC<ResourceConsentSectionProps> = ({ resourceUid, data, consents, onChanged }) => {
  const { getResourceConsent } = useApi();
  const [details, setDetails] = useState<ResourceConsentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Kept after close so the dialog's copy doesn't flip during its exit transition.
  const [recording, setRecording] = useState<{ open: boolean; action: RecordSmsConsentPayload['action'] }>({
    open: false,
    action: 'GRANTED'
  });

  const fetchRef = useRef(getResourceConsent);
  fetchRef.current = getResourceConsent;
  const requestId = useRef(0);

  const load = useCallback(() => {
    if (!resourceUid) return;
    const ticket = ++requestId.current;
    setLoading(true);
    setError(null);
    fetchRef
      .current(resourceUid)
      .then((response: any) => {
        if (ticket === requestId.current) setDetails(unwrapEnvelope<ResourceConsentDetails>(response));
      })
      .catch((err: any) => {
        if (ticket !== requestId.current) return;
        // Outside the caller's client: there is nothing this user may see.
        if (err?.statusCode === 403) setHidden(true);
        else setError(errorMessage(err, 'Could not load consent.'));
      })
      .finally(() => {
        if (ticket === requestId.current) setLoading(false);
      });
  }, [resourceUid]);

  useEffect(() => {
    setHidden(false);
    setDetails(null);
    load();
  }, [load]);

  if (hidden) return null;

  const phone = personPhone(data);
  const name = personName(data);
  const showSms = Boolean(phone) && details?.smsEnabled === true;
  const smsState: ConsentState | undefined = details?.current?.sms;

  // The number the latest grant covers, to flag a changed phone number.
  const latestSmsGrant = [...(details?.history ?? [])]
    .filter((r) => r.type === 'SMS' && r.action === 'GRANTED')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
  const consentedPhone = latestSmsGrant?.phoneNumber
    ? `${latestSmsGrant.countryCode ? `+${String(latestSmsGrant.countryCode).replace(/^\+/, '')} ` : ''}${latestSmsGrant.phoneNumber}`
    : consents?.sms?.phoneNumber || '';
  const phoneChanged = smsState?.status === 'ACCEPTED' && Boolean(consentedPhone) && !samePhone(consentedPhone, phone);

  const row = (key: string, title: string, state: ConsentState | undefined, enabled: boolean, extra?: React.ReactNode) => {
    const described = describeConsent(state, key === 'sms' ? 'SMS' : 'PRIVACY_TERMS', enabled);
    return (
      <Box key={key} sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2">{title}</Typography>
          <Chip size="small" label={described.label} color={described.color} variant="outlined" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {described.detail}
        </Typography>
        {extra}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Divider sx={{ mb: 2 }} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Consent</Typography>
        {details && (
          <Link component="button" type="button" variant="body2" onClick={() => setHistoryOpen(true)}>
            View consent history
          </Link>
        )}
      </Stack>

      {loading && !details && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} aria-label="Loading consent" />
        </Box>
      )}
      {error && !details && <Alert severity="error">{error}</Alert>}

      {details && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
          {row('privacyTerms', CONSENT_TYPE_LABELS.PRIVACY_TERMS, details.current?.privacyTerms, details.privacyTermsEnabled)}
          {showSms && (
            <>
              <Divider />
              {row(
                'sms',
                CONSENT_TYPE_LABELS.SMS,
                smsState,
                true,
                <>
                  {phoneChanged && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Consent was given for {consentedPhone}, which differs from the current number {phone}.
                    </Alert>
                  )}
                  <PermissionWrapper permission={PERMISSIONS.ADD_EDIT_RESOURCE}>
                    <Box sx={{ mt: 1.5 }}>
                      {smsState?.status === 'ACCEPTED' ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => setRecording({ open: true, action: 'WITHDRAWN' })}
                        >
                          Record withdrawal
                        </Button>
                      ) : (
                        <Button size="small" variant="contained" onClick={() => setRecording({ open: true, action: 'GRANTED' })}>
                          Record SMS consent
                        </Button>
                      )}
                    </Box>
                  </PermissionWrapper>
                </>
              )}
            </>
          )}
        </Box>
      )}

      <ConsentHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        resourceUid={resourceUid}
        personName={name}
        details={details}
      />
      <RecordSmsConsentDialog
        open={recording.open}
        action={recording.action}
        resourceUid={resourceUid}
        personName={name}
        phone={phone}
        onClose={() => setRecording((prev) => ({ ...prev, open: false }))}
        onRecorded={() => {
          load();
          onChanged?.();
        }}
      />
    </Box>
  );
};

export default ResourceConsentSection;
